/**
 * Quick-filter rail v2 (Iter 67n).
 *
 * Visual refresh of the preset tiles — same declarative `match(car) => bool`
 * API, but with chunkier, more playful icon "stickers":
 *   - Each medallion gets a layered gradient ball with a glossy highlight at
 *     11 o'clock and a soft drop shadow underneath, simulating the 3D emoji
 *     look from the reference image.
 *   - Floating decorative dots behind the icon add depth without needing
 *     bespoke artwork.
 *   - Hover wiggles the icon and lifts the whole tile.
 *   - Active state pops the tile up with an indigo ring + saturated bg.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Wallet, ShieldCheck, CreditCard, Car, Sparkles, Mountain, Coins, Trophy, MapPin, PawPrint, Cigarette, Infinity as InfinityIcon,
} from "lucide-react";

const PRESETS = [
  {
    id: "no_deposit", title: "No deposit",
    icon: Wallet,
    sticker: "from-orange-400 to-amber-500",
    cardBg:  "from-orange-50 via-white to-amber-50/60",
    border:  "border-orange-100",
    match: (c) => Number(c?.deposit || 0) === 0,
  },
  {
    id: "full_insurance", title: "Full insurance",
    icon: ShieldCheck,
    sticker: "from-emerald-400 to-teal-500",
    cardBg:  "from-emerald-50 via-white to-teal-50/60",
    border:  "border-emerald-100",
    match: (c) => Array.isArray(c?.insurance_plans) && c.insurance_plans.some((p) => /full|premium|all\s*risk/i.test(p?.name || "")),
  },
  {
    id: "no_credit_card", title: "No credit card",
    icon: CreditCard,
    sticker: "from-yellow-300 to-amber-500",
    cardBg:  "from-yellow-50 via-white to-amber-50/60",
    border:  "border-amber-100",
    showSlash: true,
    match: (c) => !!c?.no_credit_card_required || !!c?.vendor?.payment_methods?.accepts_cash,
  },
  {
    // Iter 68h — Unlimited km filter chip. Mirrors the hero trust pill
    // and the CarCardBadges "Unlimited km" badge — vendors opt-in via
    // CarForm → Mileage policy → "Unlimited mileage".
    id: "unlimited_km", title: "Unlimited km",
    icon: InfinityIcon,
    sticker: "from-emerald-400 to-teal-500",
    cardBg:  "from-emerald-50 via-white to-teal-50/60",
    border:  "border-emerald-100",
    match: (c) => c?.mileage_policy === "unlimited",
  },
  {
    id: "suv", title: "SUV",
    icon: Car,
    sticker: "from-slate-500 to-slate-700",
    cardBg:  "from-slate-50 via-white to-neutral-50/60",
    border:  "border-slate-200",
    match: (c) => c?.car_type === "SUV" || c?.car_type === "SUV 4x4",
  },
  {
    id: "new_cars", title: "New cars", subtitle: "under 5 years",
    icon: Sparkles,
    sticker: "from-rose-400 to-fuchsia-500",
    cardBg:  "from-rose-50 via-white to-fuchsia-50/60",
    border:  "border-rose-100",
    badge: "NEW",
    match: (c) => {
      const y = Number(c?.year || 0);
      return y > 0 && (new Date().getFullYear() - y) <= 5;
    },
  },
  {
    id: "awd", title: "All-wheel drive",
    icon: Mountain,
    sticker: "from-indigo-500 to-blue-600",
    cardBg:  "from-indigo-50 via-white to-blue-50/60",
    border:  "border-indigo-100",
    match: (c) => c?.car_type === "SUV 4x4",
  },
  {
    id: "low_deposit", title: "Low deposit", subtitle: "under €100",
    icon: Coins,
    sticker: "from-amber-400 to-orange-500",
    cardBg:  "from-amber-50 via-white to-orange-50/60",
    border:  "border-amber-100",
    match: (c) => {
      const d = Number(c?.deposit || 0);
      return d > 0 && d < 100;
    },
  },
  {
    id: "top_cars", title: "Top cars", subtitle: "4.5★ and up",
    icon: Trophy,
    sticker: "from-violet-500 to-fuchsia-600",
    cardBg:  "from-violet-50 via-white to-fuchsia-50/60",
    border:  "border-violet-100",
    match: (c) => Number(c?.rating || 0) >= 4.5,
  },
  {
    id: "city_delivery", title: "City delivery",
    icon: MapPin,
    sticker: "from-rose-500 to-red-600",
    cardBg:  "from-rose-50 via-white to-red-50/60",
    border:  "border-rose-100",
    match: (c) => Array.isArray(c?.pickup_locations) && c.pickup_locations.length >= 2,
  },
  {
    // Iter 67bp — Pet-friendly preset. The match is straight off the
    // boolean we ship from /api/cars; legacy listings without the field
    // are treated as "not pet-friendly" (`!!undefined === false`).
    id: "pet_friendly", title: "Pet-friendly",
    icon: PawPrint,
    sticker: "from-emerald-400 to-green-600",
    cardBg:  "from-emerald-50 via-white to-green-50/60",
    border:  "border-emerald-100",
    match: (c) => !!c?.pet_friendly,
  },
  {
    id: "smoking_allowed", title: "Smoking allowed",
    icon: Cigarette,
    sticker: "from-amber-400 to-orange-500",
    cardBg:  "from-amber-50 via-white to-orange-50/60",
    border:  "border-amber-100",
    match: (c) => !!c?.smoking_allowed,
  },
];

export const QUICK_PRESETS = PRESETS;
export const QUICK_PRESET_BY_ID = Object.fromEntries(PRESETS.map((p) => [p.id, p]));

// One reusable "sticker" medallion. Layered: outer soft shadow halo, gradient
// ball, glossy white highlight at 11 o'clock, then the lucide icon centred.
// Iter 67dq — `size` controls mobile vs desktop scale (compact grid uses 48px).
function StickerIcon({ icon: Icon, gradient, badge, showSlash, wiggle, size = "lg" }) {
  // Iter 74q → 74r — mobile shrunk further per user feedback (40→32),
  // desktop kept at 56 so it stays legible on wide screens.
  const ball = size === "sm" ? "h-[32px] w-[32px]" : "h-[56px] w-[56px]";
  const iconSize = size === "sm" ? "h-[14px] w-[14px]" : "h-[26px] w-[26px]";
  const halo = size === "sm" ? "h-1 w-5 -bottom-0.5" : "h-2 w-9 -bottom-1";
  return (
    <div className="relative">
      {/* drop-shadow halo under the ball */}
      <div className={`absolute ${halo} left-1/2 -translate-x-1/2 rounded-full bg-black/10 blur-md`} aria-hidden="true" />
      {/* gradient ball */}
      <div className={`relative ${ball} rounded-full bg-gradient-to-br ${gradient} shadow-[0_6px_18px_rgba(0,0,0,0.12)] flex items-center justify-center ${wiggle ? "group-hover:animate-[wiggle_700ms_ease-in-out]" : ""} transition-transform duration-300 group-hover:scale-[1.06]`}>
        {/* glossy highlight */}
        <span
          className="absolute top-1.5 left-2 h-2 w-3 rounded-full bg-white/55 blur-[3px]"
          aria-hidden="true"
        />
        <Icon className={`${iconSize} text-white drop-shadow-sm`} strokeWidth={2} />
        {showSlash && (
          <span
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <span className="block h-[2px] w-[90%] -rotate-[18deg] rounded-full bg-rose-600 shadow-[0_0_0_1px_rgba(255,255,255,0.7)]" />
          </span>
        )}
      </div>
      {badge && (
        <span className={`absolute -top-1 -right-1 px-1 py-0.5 rounded-full bg-rose-500 text-white ${size === "sm" ? "text-[7px]" : "text-[9px]"} font-extrabold tracking-wide shadow-md rotate-[8deg]`}>
          {badge}
        </span>
      )}
    </div>
  );
}

export default function QuickFilterRail({ activeId, onSelect }) {
  const [allCars, setAllCars] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/cars").then((r) => { if (!cancelled) setAllCars(r.data || []); }).catch(() => { if (!cancelled) setAllCars([]); });
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    if (!allCars) return {};
    const out = {};
    for (const p of PRESETS) out[p.id] = allCars.filter((c) => p.match(c)).length;
    return out;
  }, [allCars]);

  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-5" data-testid="quick-filter-rail">
      {/* Iter 67dq — mobile compact grid (6 cols × 2 rows), desktop keeps
          the horizontal scroll rail. */}
      <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 gap-2 sm:gap-3 lg:hidden">
        {PRESETS.map((p) => {
          const isActive = activeId === p.id;
          const count = counts[p.id];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect?.(isActive ? null : p.id)}
              className={`group relative rounded-2xl border bg-gradient-to-b ${p.cardBg} text-center transition-all duration-300 ${
                isActive
                  ? `${p.border} ring-2 ring-offset-1 ring-amber-500 shadow-lg -translate-y-0.5`
                  : `${p.border} hover:shadow-md`
              }`}
              data-testid={`quick-preset-${p.id}`}
            >
              <div className="px-1.5 pt-2 pb-1.5 flex flex-col items-center">
                <StickerIcon
                  icon={p.icon}
                  gradient={p.sticker}
                  badge={p.badge}
                  showSlash={p.showSlash}
                  size="sm"
                />
                <div className="mt-1.5 font-display text-[9.5px] font-semibold leading-tight text-neutral-900 line-clamp-2 min-h-[22px]">
                  {p.title}
                </div>
                <div
                  className={`mt-0.5 inline-flex items-center justify-center min-w-[1.5rem] h-[16px] px-1 rounded-full text-[9px] font-extrabold tracking-wide transition ${
                    isActive
                      ? "bg-neutral-900 text-amber-400 shadow"
                      : "bg-white text-neutral-800 ring-1 ring-neutral-200"
                  }`}
                  data-testid={`quick-preset-count-${p.id}`}
                >
                  {allCars === null ? "…" : (count || 0)}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Desktop horizontal scroll rail (unchanged) */}
      <div className="hidden lg:flex items-stretch gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {PRESETS.map((p) => {
          const isActive = activeId === p.id;
          const count = counts[p.id];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect?.(isActive ? null : p.id)}
              className={`group relative shrink-0 w-[128px] rounded-3xl border bg-gradient-to-b ${p.cardBg} text-left transition-all duration-300 ${
                isActive
                  ? `${p.border} ring-2 ring-offset-2 ring-indigo-500 shadow-xl -translate-y-1`
                  : `${p.border} hover:shadow-lg hover:-translate-y-1`
              }`}
              data-testid={`quick-preset-desktop-${p.id}`}
            >
              {/* Decorative floating dots — give the tile depth without an asset */}
              <span className="pointer-events-none absolute top-3 right-4 h-1.5 w-1.5 rounded-full bg-white/70 shadow-sm" aria-hidden="true" />
              <span className="pointer-events-none absolute top-7 right-10 h-1 w-1 rounded-full bg-white/60" aria-hidden="true" />
              <span className="pointer-events-none absolute bottom-8 left-4 h-2 w-2 rounded-full bg-white/50 blur-[1px]" aria-hidden="true" />

              <div className="px-3 pt-3 pb-3 flex flex-col items-center text-center h-[168px]">
                <div className="min-h-[34px] flex flex-col items-center">
                  <div className="font-display text-[13px] font-semibold leading-tight text-neutral-900">
                    {p.title}
                  </div>
                  {p.subtitle && (
                    <div className="text-[9px] text-neutral-500 leading-tight mt-0.5">{p.subtitle}</div>
                  )}
                </div>

                <div className="my-auto pt-2">
                  <StickerIcon
                    icon={p.icon}
                    gradient={p.sticker}
                    badge={p.badge}
                    showSlash={p.showSlash}
                    wiggle
                  />
                </div>

                <div
                  className={`mt-2 inline-flex items-center justify-center min-w-[2.25rem] h-[22px] px-2 rounded-full text-[10px] font-extrabold tracking-wide transition ${
                    isActive
                      ? "bg-indigo-600 text-white shadow-md"
                      : "bg-white text-neutral-800 ring-1 ring-neutral-200 shadow-sm"
                  }`}
                  data-testid={`quick-preset-count-desktop-${p.id}`}
                >
                  {allCars === null ? "…" : (count || 0)}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
